import React, { Component } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper } from "@mui/material";
import {
    accPayEntities, reportColumnChunks, formatAccounting, formatPaddedDate, formatPaddedWeek,
    normalizeWeekCode, unapprovedWeekMap, weekStartDate,
} from '../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';
import TotalsBar from '../../Pages/admin/shared/TotalsBar';
import NotApprovedTag from '../../Pages/admin/shared/NotApprovedTag';

/* Account Balance — Detail view.
 *
 * Laid out after the legacy report the user supplied (phone-clinic/reportLayout/accountbalance.html,
 * T1.36, 2026-10-26), in the app's current theme:
 *   1. Shops Profit & Loss — Week + 9 shops per block, a Total row per block;
 *   2. Shops Card Amount   — same shape, only when the account has card shops;
 *   3. ONE table holding Payment By Account (5 columns) beside Payment By Shop (6 columns), a navy
 *      "Week : …" bar per week across both, the two sides paired line-by-line within a week;
 *   4. the Balance B/F | P&L | Card | Pay By Acc | Pay By Shop | Net Balance line.
 * Negative amounts are shown in brackets and dates as "06 Sep 2026" (both display only).
 *
 * Every figure is the one index.js already computed — this component only lays them out.
 */

const SHOPS_PER_BLOCK = 9;
/* The legacy file's Week column is 13%; this one is 17% because it also carries the "Not approved"
   tag (T1.35), which wraps under the dates instead of spilling into the first shop column. */
const WEEK_COL_WIDTH = '17%';
const SHOP_COL_WIDTH = `${(83 / SHOPS_PER_BLOCK).toFixed(1)}%`;

const NUM = { textAlign: 'right', whiteSpace: 'nowrap' };
const WEEK_BAR = { background: '#19284a', color: '#ffffff', fontSize: 12, fontWeight: 700 };
const TOTAL_ROW = { background: '#e7e7e7' };
// Separates Payment By Account (left) from Payment By Shop (right), header to footer.
const DIVIDER = { borderLeft: '2px solid #cbd5e1' };
const BOLD = { fontWeight: 800 };
const CELL = { fontSize: 12, overflowWrap: 'anywhere' };
// Date · Shop · On Behalf Acc · Description · Amount | Date · Pay Account · Shop · On Behalf Acc · Description · Amount
const PAYMENT_COL_WIDTHS = ['7%', '9%', '9%', '16%', '7%', '7%', '8%', '8%', '8%', '14%', '7%'];
const TAG = { fontSize: 10, color: '#64748b', marginLeft: 4, textTransform: 'uppercase', letterSpacing: 0.3 };

/* A Shop Payments line's stored date, but only when it falls inside the line's own week. Only 22 of
   1,742 stored dates do — the rest look like entry dates — so anything else is left blank rather
   than shown as a misleading payment day (user's choice, 2026-10-26). Ledger rows have no date. */
function dateInsideWeek(date, weekCode) {
    if (!date || !weekCode) return '';
    const monday = weekStartDate(normalizeWeekCode(weekCode));
    if (!monday) return '';
    const sunday = new Date(`${monday}T00:00:00`);
    sunday.setDate(sunday.getDate() + 6);
    const sun = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
    const day = String(date).slice(0, 10);
    return day >= monday && day <= sun ? formatPaddedDate(day) : '';
}

// A warehouse in an account / shop column, labelled so it is not mistaken for one.
const withWarehouseLabel = (name) => (name ? <>{name}<span style={TAG}>Warehouse</span></> : '');

class Detail extends Component {

    // Week code -> not-approved entry (T1.35). Keys are normalised, so look up with any form.
    unapproved() {
        const list = this.props.ReportDetails?.unapprovedWeeks;
        if (this._unapprovedList !== list) {
            this._unapprovedList = list;
            this._unapprovedMap = unapprovedWeekMap(list);
        }
        return this._unapprovedMap;
    }

    /*
     * One wide table, split into stacked blocks of the Week column plus 9 shops (the legacy
     * layout). `columns`, each row's `PnL`, and `totals` are PARALLEL arrays indexed by column, so
     * a block is the same index range sliced out of all three. When there is more than one block
     * the last is padded with blank columns, so every block's columns line up.
     */
    renderWideTable({ columns, rows, totals, columnLabel }) {
        const cols = columns || [];
        const multiBlock = cols.length > SHOPS_PER_BLOCK;
        return reportColumnChunks(cols.length, SHOPS_PER_BLOCK + 1).map(({ start, end }) => {
            const pad = multiBlock ? SHOPS_PER_BLOCK - (end - start) : 0;
            const blanks = (key) => Array.from({ length: pad }, (_, i) => <TableCell key={`${key}-pad-${i}`} />);
            const shopWidth = multiBlock ? { width: SHOP_COL_WIDTH } : {};
            return (
                <Row key={`block-${start}`} >
                    <Col md={12} >
                        <Paper>
                            <TableContainer className="report-table-container" >
                                <Table size="small" className="report-table" aria-label="shops profit and loss"
                                    style={multiBlock ? { tableLayout: 'fixed' } : undefined}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className="report-label-cell" style={{ color: 'white', width: WEEK_COL_WIDTH }} >Week</TableCell>
                                            {cols.slice(start, end).map((x, i) => (
                                                <TableCell key={start + i} style={{ color: 'white', ...shopWidth }} >{columnLabel(x)}</TableCell>
                                            ))}
                                            {blanks('h')}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(rows || []).map((x, idx) => (
                                            <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} >
                                                <TableCell className="report-label-cell" style={{ whiteSpace: 'normal' }} >
                                                    <span style={{ whiteSpace: 'nowrap' }}>{formatPaddedWeek(x.weekNum)}</span>
                                                    <NotApprovedTag info={this.unapproved().get(normalizeWeekCode(x.weekNum))} />
                                                </TableCell>
                                                {x.noApprovedSale
                                                    // Placeholder for a not-approved week — no figures (T1.35).
                                                    ? cols.slice(start, end).map((c, i) => (
                                                        <TableCell key={start + i} style={{ ...NUM, color: '#94a3b8' }}>—</TableCell>
                                                    ))
                                                    : cols.slice(start, end).map((c, i) => (
                                                        <TableCell key={start + i} style={NUM}>{formatAccounting((x.PnL || [])[start + i])}</TableCell>
                                                    ))}
                                                {blanks(`r${idx}`)}
                                            </TableRow>
                                        ))}
                                        <TableRow style={TOTAL_ROW} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} >
                                            <TableCell className="report-label-cell" style={BOLD} >Total</TableCell>
                                            {(totals || []).slice(start, end).map((xl, i) => (
                                                <TableCell style={{ ...BOLD, ...NUM }} key={start + i}>{formatAccounting(isNaN(xl?.totalValue) ? 0 : parseFloat(xl.totalValue))}</TableCell>
                                            ))}
                                            {blanks('t')}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Col>
                </Row>
            );
        });
    }

    // Payment By Account lines grouped by week code. Zero-amount lines are left out, as before.
    payByAccountByWeek() {
        const byWeek = new Map();
        for (const block of (this.props.ReportDetails?.payByAccountList || [])) {
            const week = normalizeWeekCode(block.AP_WEEK);
            const lines = (block.AP_DETAILS || []).filter(l => l.ShopPay_Amnt !== 0);
            byWeek.set(week, [...(byWeek.get(week) || []), ...lines]);
        }
        return byWeek;
    }

    // Payment By Shop rows grouped by week code.
    payByShopByWeek() {
        const byWeek = new Map();
        for (const block of (this.props.ReportDetails?.payByShopList || [])) {
            const week = normalizeWeekCode(block.TR_WEEK);
            byWeek.set(week, [...(byWeek.get(week) || []), ...(block.SP_DETAILS || [])]);
        }
        return byWeek;
    }

    // The five Payment By Account cells for one line, or five blanks.
    accountCells(line, key) {
        if (!line) return Array.from({ length: 5 }, (_, i) => <TableCell key={`${key}-a${i}`} />);
        const { shop, acc, wh } = accPayEntities(line);
        return [
            <TableCell key={`${key}-a0`} style={CELL} >{formatPaddedDate(line.ShopPay_Date) || line.ShopPay_Date}</TableCell>,
            <TableCell key={`${key}-a1`} style={CELL} >{shop}</TableCell>,
            <TableCell key={`${key}-a2`} style={CELL} >{acc}{acc && wh ? ', ' : ''}{withWarehouseLabel(wh)}</TableCell>,
            <TableCell key={`${key}-a3`} style={CELL} >{line.ShopPay_Desc}</TableCell>,
            <TableCell key={`${key}-a4`} style={{ ...CELL, ...NUM }} >{formatAccounting(line.ShopPay_Amnt)}</TableCell>,
        ];
    }

    /* The six Payment By Shop cells for one row, or six blanks. Columns follow legacy
     * SP_MS_FMS_RptAccountTransactions_Balance_Query: a shop paying this account fills Shop; an
     * account paying on this account's behalf fills Pay Account, with this account as On Behalf
     * Acc; a warehouse (none exist today) goes in Shop, labelled. */
    shopCells(row, key) {
        if (!row) return Array.from({ length: 6 }, (_, i) => <TableCell key={`${key}-s${i}`} style={i === 0 ? DIVIDER : undefined} />);
        const type = String(row.TR_TYPE);
        const payAccount = type === '2' ? row.ACC_ID : '';
        const shop = type === '3' ? row.ACC_ID : (type === '4' ? withWarehouseLabel(row.ACC_ID) : '');
        const onBehalf = type === '2' ? (this.props.ReportDetails?.accountTitle || '') : '';
        return [
            <TableCell key={`${key}-s0`} style={{ ...CELL, ...DIVIDER }} >{dateInsideWeek(row.TR_DATE, row.TR_WEEK)}</TableCell>,
            <TableCell key={`${key}-s1`} style={CELL} >{payAccount}</TableCell>,
            <TableCell key={`${key}-s2`} style={CELL} >{shop}</TableCell>,
            <TableCell key={`${key}-s3`} style={CELL} >{onBehalf}</TableCell>,
            <TableCell key={`${key}-s4`} style={CELL} >{row.TR_DET}</TableCell>,
            <TableCell key={`${key}-s5`} style={{ ...CELL, ...NUM }} >{formatAccounting(row.TR_AMT)}</TableCell>,
        ];
    }

    renderPaymentsTable() {
        const left = this.payByAccountByWeek();
        const right = this.payByShopByWeek();
        const weeks = [...new Set([...left.keys(), ...right.keys()])].filter(Boolean).sort();
        const head = { color: 'white', fontSize: 12 };
        const body = [];
        weeks.forEach((week) => {
            const a = left.get(week) || [];
            const s = right.get(week) || [];
            body.push(
                <TableRow key={`wk-${week}`} className="table-rows" style={{ background: WEEK_BAR.background }} >
                    <TableCell colSpan={11} style={WEEK_BAR} >
                        Week : {formatPaddedWeek(week)}
                        <NotApprovedTag info={this.unapproved().get(week)} />
                    </TableCell>
                </TableRow>
            );
            for (let i = 0; i < Math.max(a.length, s.length); i++) {
                body.push(
                    <TableRow key={`${week}-${i}`} className="table-rows" >
                        {this.accountCells(a[i], `${week}-${i}`)}
                        {this.shopCells(s[i], `${week}-${i}`)}
                    </TableRow>
                );
            }
        });

        return (
            <Row>
                <Col md={12} >
                    <Paper>
                        <TableContainer className="report-table-container" >
                            <Table className="report-table" size="small" aria-label="payment by account and payment by shop" style={{ tableLayout: 'fixed' }}>
                                {/* Fixed layout takes widths from the FIRST row, which here is the two
                                    spanning "Payment By …" cells — so the widths live on a colgroup. */}
                                <colgroup>
                                    {PAYMENT_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                                </colgroup>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" colSpan={5} style={{ color: 'white', fontWeight: 700 }} >Payment By Account</TableCell>
                                        <TableCell align="center" colSpan={6} style={{ color: 'white', fontWeight: 700, ...DIVIDER }} >Payment By Shop</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...head }} >Date</TableCell>
                                        <TableCell style={{ ...head }} >Shop</TableCell>
                                        <TableCell style={{ ...head }} >On Behalf Acc</TableCell>
                                        <TableCell style={{ ...head }} >Description</TableCell>
                                        <TableCell style={{ ...head, ...NUM }} >Amount</TableCell>
                                        <TableCell style={{ ...head, ...DIVIDER }} >Date</TableCell>
                                        <TableCell style={{ ...head }} >Pay Account</TableCell>
                                        <TableCell style={{ ...head }} >Shop</TableCell>
                                        <TableCell style={{ ...head }} >On Behalf Acc</TableCell>
                                        <TableCell style={{ ...head }} >Description</TableCell>
                                        <TableCell style={{ ...head, ...NUM }} >Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {body.length === 0 && (
                                        <TableRow><TableCell colSpan={11} align="center" style={{ color: '#64748b' }} >No payments in this range.</TableCell></TableRow>
                                    )}
                                    {body}
                                </TableBody>
                                <TableFooter>
                                    <TableRow style={TOTAL_ROW} >
                                        <TableCell colSpan={4} style={{ ...BOLD, color: '#111827' }} >Total</TableCell>
                                        <TableCell style={{ ...BOLD, ...NUM, color: '#111827' }} >{formatAccounting(this.props.ReportDetails?.pbaTotal)}</TableCell>
                                        <TableCell colSpan={5} style={{ ...BOLD, ...DIVIDER, color: '#111827' }} >Total</TableCell>
                                        <TableCell style={{ ...BOLD, ...NUM, color: '#111827' }} >{formatAccounting(this.props.ReportDetails?.pbsTotal)}</TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Col>
            </Row>
        );
    }

    render() {
        const d = this.props.ReportDetails || {};
        // Same expression as before, character for character (index.js toSummaryRow mirrors it).
        const net = (d.totalPnL + d.pbaTotal + +d.FB) - parseFloat(d.totalCardSum).toFixed(2) - d.pbsTotal;
        const sep = <span style={{ borderRight: '1px solid', margin: '0px 5px' }} ></span>;
        return (
            <section>
                <SectionHeader title={<>Shops Profit & Loss</>} />
                {this.renderWideTable({
                    columns: d.shopDataList,
                    rows: d.weekDaysList,
                    totals: d.totalShopCash,
                    columnLabel: (x) => x?.ShopName?.label
                })}

                {d.cardWeekList?.length > 0 ?
                    <div>
                        <SectionHeader title={<>Shops Card Amount</>} />
                        {this.renderWideTable({
                            columns: d.cardShops,
                            rows: d.cardWeekList,
                            totals: d.cardTotal,
                            columnLabel: (x) => x
                        })}
                    </div>
                    :
                    null
                }

                <SectionHeader title={<>Payment By Account &nbsp;|&nbsp; Payment By Shop</>} />
                {this.renderPaymentsTable()}

                <TotalsBar>
                    <span>Balance B/F: </span>
                    <span>{formatAccounting(d.FB)}</span>
                    {sep}
                    <span>P&L: </span>
                    <span>{formatAccounting(d.totalPnL)}</span>
                    {sep}
                    <span>Card: </span>
                    <span>{formatAccounting(d.totalCardSum)}</span>
                    {sep}
                    <span>Pay By Acc: </span>
                    <span>{formatAccounting(d.pbaTotal)}</span>
                    {sep}
                    <span>Pay By Shop: </span>
                    <span>{formatAccounting(d.pbsTotal)}</span>
                    {sep}
                    <span style={BOLD} >Net Balance: </span>
                    <span style={BOLD} >{formatAccounting(net)}</span>
                </TotalsBar>
            </section >
        );
    }
}

export default Detail;
